import { useCompanySettingByNameQuery } from "graphql/query/company.setting.by.name.query";
import { useFeatureFlagPayload } from "posthog-js/react";
import { useCallback, useEffect, useState } from "react";
import { useAppSelector } from "store";
import {
  getSettingValue,
  settingRegistry,
} from "../components/company/settings/settings.registry";
import { useCreateCompanySettingMutation } from "../graphql/mutation/company/create.company.setting.mutation";
import { useDeleteCompanySettingMutation } from "../graphql/mutation/company/delete.company.setting.mutation";
import { useUpdateCompanySettingMutation } from "../graphql/mutation/company/update.company.setting.mutation";
import { useSystemSettingValueQuery } from "../graphql/system/setting/system.setting.value.query";

interface Settings<T> {
  value: T;
  loading: boolean;
  update: (data: T) => Promise<void>;
  remove: () => Promise<void>;
  extra?: {
    url?: string;
  };
}

export const useCompanySetting = <T extends unknown>(
  settingName: string,
  onComplete?: (value: T) => void,
  defaultValue?: T,
  skipSystemValueIfNull?: boolean,
): Settings<T> => {
  const [value, setValue] = useState<T>(
    settingRegistry[settingName]?.default as T,
  );
  const companyId = useAppSelector((state) => state.company.id);
  const variant = useFeatureFlagPayload(settingName);

  const [createMutation] = useCreateCompanySettingMutation();
  const [updateMutation] = useUpdateCompanySettingMutation();
  const [deleteMutation] = useDeleteCompanySettingMutation();
  const { loading, data } = useCompanySettingByNameQuery({
    skip: !companyId,
    variables: {
      company: companyId,
      name: settingName,
    },
  });

  useEffect(() => {
    if (!data) return;

    if (data && data.setting === null) {
      return;
    }

    if (!data || data.setting.value === null) {
      if (onComplete && defaultValue !== undefined) {
        onComplete(defaultValue);
      }
      return;
    }

    const settingValue = getSettingValue<T>(settingName, data.setting.value);

    setValue(settingValue);

    if (onComplete) {
      onComplete(settingValue);
    }
  }, [data]);

  const { loading: fallbackLoading, data: fallbackData } =
    useSystemSettingValueQuery({
      skip: Boolean(skipSystemValueIfNull) || loading || Boolean(data?.setting),
      variables: {
        name: settingName,
      },
    });

  useEffect(() => {
    if (!fallbackData) return;

    if (!fallbackData || fallbackData.setting === null) {
      if (onComplete && defaultValue !== undefined) {
        onComplete(defaultValue);
      }
      return;
    }

    const settingValue = getSettingValue<T>(settingName, fallbackData.setting);

    setValue(settingValue);

    if (onComplete) {
      onComplete(settingValue);
    }
  }, [fallbackData]);

  // TODO load fallback

  useEffect(() => {
    const isNotUsingCompanySetting =
      data?.setting === null || data?.setting?.value === null;
    if (isNotUsingCompanySetting && variant) {
      setValue(variant as T);
      return;
    }

    const settingValue = getSettingValue<T>(settingName, data?.setting?.value);
    setValue(settingValue);
  }, [variant, data?.setting, settingName]);

  const remove = useCallback(async () => {
    setValue(null);

    if (!data?.setting?.id) {
      return;
    }

    try {
      await deleteMutation({
        variables: {
          id: data?.setting.id,
        },
      });
    } catch (e) {
      // revert the current setting
      setValue(value);
      throw e;
    }
  }, [data?.setting, value]);

  const update = useCallback(
    async (next: T) => {
      setValue(next);

      try {
        if (data?.setting?.id) {
          await updateMutation({
            variables: {
              id: data?.setting.id,
              input: {
                value: JSON.stringify(next),
              },
            },
          });
        } else {
          await createMutation({
            variables: {
              company: companyId,
              input: {
                name: settingName,
                value: JSON.stringify(next),
              },
            },
          });
        }
      } catch (e) {
        // revert the current setting
        setValue(value);
        throw e;
      }
    },
    [data?.setting, value, settingName, companyId],
  );

  return {
    extra: {
      url: data?.setting?.fileReadUrl,
    },
    loading: loading || fallbackLoading,
    remove,
    update,
    value,
  };
};
